import type { Contact, ItemPayload, PayloadOf } from './models';

/** The best single line to show for a person. */
export const contactDisplay = (c: Contact) => [c.name, c.company].filter((s) => s && s.length > 0).join(' · ');

function rawPrice(raw: Record<string, unknown>, key: string): Record<string, unknown> | undefined {
  const prices = raw.prices;
  if (!Array.isArray(prices)) return undefined;
  const found = prices.find((p) => typeof p === 'object' && p !== null && (p as { key?: unknown }).key === key);
  return found as Record<string, unknown> | undefined;
}

const defined = (o: Record<string, unknown>) =>
  Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined && v !== null));

/** The JSON to send when approving an edited version: the original plus the editable fields. */
export function payloadToJson(p: ItemPayload): Record<string, unknown> {
  switch (p.type) {
    case 'social_post':
      return defined({ ...p.raw, text: p.text, first_comment: p.firstComment });
    case 'message_reply':
      return { ...p.raw, text: p.text };
    case 'offer':
      return defined({
        ...p.raw,
        summary: p.summary,
        message: p.message,
        prices: p.prices.map((l) =>
          defined({
            ...rawPrice(p.raw, l.key),
            key: l.key,
            label: l.label,
            amount: l.amount,
            recurring: l.monthly ? 'monthly' : undefined,
            optional: l.optional ? true : undefined,
          }),
        ),
      });
    case 'deliverable':
      return defined({ ...p.raw, message: p.message });
    case 'document':
      return { ...p.raw, title: p.title, body: p.body };
    case 'hidden':
      return {};
    default:
      return p.raw;
  }
}

/** Total of one-off prices, for offers. */
export const oneOffTotal = (p: PayloadOf<'offer'>) =>
  p.prices.filter((l) => !l.monthly && !l.optional).reduce((sum, l) => sum + l.amount, 0);

/** What a person pastes when publishing by hand: the text, then the link unless the text has it. */
export const pasteText = (p: PayloadOf<'social_post'>) =>
  !p.link || p.text.includes(p.link) ? p.text : `${p.text}\n\n${p.link}`;

/** A post for a group someone else runs (a person pastes it there), not for one's own page. */
export const isForGroup = (p: PayloadOf<'social_post'>) => p.targetUrl?.includes('/groups/') ?? false;

/** The page where the post goes: its web address, or a subreddit's page when only `r/name` is known. */
export function targetUri(p: PayloadOf<'social_post'>): string | undefined {
  if (p.targetUrl && /^https?:\/\//.test(p.targetUrl)) return p.targetUrl;
  if (p.channel === 'reddit' && p.target) {
    const name = /^\/?r\/(\w+)\/?$/.exec(p.target.trim())?.[1];
    if (name) return `https://www.reddit.com/r/${name}/`;
  }
  return undefined;
}

/** A `mailto:` address with the subject and body filled in. Spaces stay `%20`: mail apps show `+` as is. */
export function mailDraft(email: string, subject: string | undefined, body: string): string {
  const query = [
    ...(subject ? [`subject=${encodeURIComponent(subject)}`] : []),
    `body=${encodeURIComponent(body)}`,
  ].join('&');
  return `mailto:${email}?${query}`;
}

/** What a person pastes when they carry the work out by hand. */
export function manualText(p: ItemPayload): string | undefined {
  if (p.type === 'social_post') return pasteText(p);
  if (p.type === 'message_reply') return p.text;
  return undefined;
}

/** Where the app takes them to do it: the group or page for a post, a ready email for a message. */
export function manualTarget(p: ItemPayload): string | undefined {
  if (p.type === 'social_post') return targetUri(p);
  if (p.type === 'message_reply' && p.to.email) return mailDraft(p.to.email, p.subject, p.text);
  return undefined;
}
